import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Mail, Phone, RefreshCw, LogOut, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import Monogram from "../components/Monogram";

export default function AdminLeadsPage() {
  const { token, logout, user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data);
    } catch (e) {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchLeads();
  }, [token, fetchLeads]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads((arr) => arr.filter((l) => l.id !== id));
      setSelected(null);
      toast.success("Lead deleted.");
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] text-ink-100" data-testid="page-admin-leads">
      <header className="border-b border-hair sticky top-0 z-40 bg-[#050505]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-4 flex items-center justify-between">
          <Monogram size={32} withWord />
          <nav className="flex items-center gap-6 text-[11px] tracking-[0.22em] uppercase">
            <Link to="/admin/leads" className="text-ink-100" data-testid="admin-nav-leads">Leads</Link>
            <Link to="/admin/blog" className="text-ink-300 hover:text-ink-100" data-testid="admin-nav-blog">Journal</Link>
            <Link to="/admin/studio" className="text-ink-300 hover:text-ink-100" data-testid="admin-nav-studio">Generator</Link>
            <Link to="/" className="text-ink-500 hover:text-ink-300" data-testid="admin-nav-site">↗ Site</Link>
            <div className="hidden md:flex items-center gap-2 text-ink-500">
              <span className="w-1.5 h-1.5 rounded-full bg-rouge" /> {user?.email}
            </div>
            <button onClick={logout} className="flex items-center gap-2 text-ink-300 hover:text-rouge" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </nav>
        </div>
      </header>

      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-ink-300 flex items-center gap-3">
              <span className="block w-8 h-px bg-rouge" /> Studio Inbox
            </div>
            <h1 className="mt-4 font-serif-editorial text-5xl lg:text-6xl tracking-tight text-ink-100">
              {leads.length} <span className="text-ink-500">lead{leads.length === 1 ? "" : "s"}</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchLeads} className="flex items-center gap-2 border border-hair hover:border-ink-100 px-4 py-2 text-[11px] tracking-[0.22em] uppercase" data-testid="admin-refresh"><RefreshCw size={13} /> Refresh</button>
            <Link to="/admin/studio" className="flex items-center gap-2 bg-rouge hover:bg-rouge-deep px-4 py-2 text-[11px] tracking-[0.22em] uppercase text-ink-100" data-testid="admin-go-studio"><Wand2 size={13} /> Generate image</Link>
          </div>
        </div>

        {loading ? (
          <div className="text-ink-500 text-sm">Loading…</div>
        ) : leads.length === 0 ? (
          <div className="border border-hair p-16 text-center" data-testid="admin-leads-empty">
            <div className="text-ink-500 text-[11px] tracking-[0.22em] uppercase">No leads yet</div>
            <p className="mt-3 text-ink-300 text-sm">Leads will appear here as soon as someone submits the contact form.</p>
          </div>
        ) : (
          <div className="border border-hair">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 text-[10px] tracking-[0.22em] uppercase text-ink-500 border-b border-hair">
              <div className="col-span-3">Name / Email</div>
              <div className="col-span-3">Project / Budget</div>
              <div className="col-span-4 hidden md:block">Message</div>
              <div className="col-span-2 text-right">Date</div>
            </div>
            {leads.map((l, i) => (
              <motion.button
                key={l.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i, 10) * 0.03 }}
                onClick={() => setSelected(l)}
                data-testid={`admin-lead-row-${i}`}
                className="w-full text-left grid grid-cols-12 gap-4 px-5 py-4 border-b border-hair last:border-b-0 hover:bg-[#0c0c0e] transition-colors"
              >
                <div className="col-span-3">
                  <div className="text-ink-100 text-sm">{l.name}</div>
                  <div className="text-ink-500 text-xs mt-1">{l.email}</div>
                </div>
                <div className="col-span-3">
                  <div className="text-ink-300 text-sm">{l.project_type}</div>
                  <div className="text-ink-500 text-xs mt-1">{l.budget || "—"}</div>
                </div>
                <div className="col-span-4 hidden md:block text-ink-300 text-xs leading-relaxed line-clamp-2">{l.message}</div>
                <div className="col-span-2 text-right text-ink-500 text-xs">{new Date(l.created_at).toLocaleString()}</div>
              </motion.button>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end md:items-center justify-center p-4"
            onClick={() => setSelected(null)}
            data-testid="admin-lead-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
              className="w-full max-w-2xl bg-[#0c0c0e] border border-hair p-8 lg:p-10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-rouge">{selected.project_type}</div>
                  <h2 className="mt-2 font-serif-editorial text-3xl text-ink-100 leading-tight">{selected.name}</h2>
                </div>
                <button onClick={() => onDelete(selected.id)} className="text-ink-500 hover:text-rouge p-2" data-testid="admin-lead-delete"><Trash2 size={16} /></button>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-ink-100 hover:text-rouge"><Mail size={14} className="text-rouge" />{selected.email}</a>
                {selected.phone && <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-ink-100 hover:text-rouge"><Phone size={14} className="text-rouge" />{selected.phone}</a>}
                <div className="text-ink-300"><span className="text-ink-500 text-[10px] tracking-[0.22em] uppercase">Budget</span><br />{selected.budget || "—"}</div>
                <div className="text-ink-300"><span className="text-ink-500 text-[10px] tracking-[0.22em] uppercase">Target date</span><br />{selected.target_date || "—"}</div>
                <div className="text-ink-300"><span className="text-ink-500 text-[10px] tracking-[0.22em] uppercase">Company</span><br />{selected.company || "—"}</div>
                <div className="text-ink-300"><span className="text-ink-500 text-[10px] tracking-[0.22em] uppercase">Locale</span><br />{selected.locale}</div>
              </div>
              <div className="border-t border-hair pt-4">
                <div className="text-[10px] tracking-[0.22em] uppercase text-ink-500 mb-2">Message</div>
                <p className="text-ink-100 whitespace-pre-wrap text-[15px] leading-relaxed">{selected.message}</p>
              </div>
              <div className="mt-8 text-[10px] tracking-[0.22em] uppercase text-ink-500">Received · {new Date(selected.created_at).toLocaleString()}</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
